import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, User, Bot, ArrowRight, Home, CreditCard, Calendar, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CFI Survey questions in conversational order
const SURVEY_STEPS = [
  {
    id: "welcome",
    type: "message",
    content: "Hey there! 👋 I'm here to help you start your journey to better credit. Let's chat!",
    delay: 500,
  },
  {
    id: "first_name",
    type: "input",
    question: "First things first - what's your first name?",
    placeholder: "Type your first name...",
    field: "first_name",
    required: true,
  },
  {
    id: "greeting",
    type: "message",
    content: (data) => `Nice to meet you, ${data.first_name}! 🌟`,
    delay: 300,
  },
  {
    id: "last_name",
    type: "input",
    question: "And your last name?",
    placeholder: "Type your last name...",
    field: "last_name",
    required: true,
  },
  {
    id: "goal_question",
    type: "choice",
    question: "What's your main goal right now?",
    field: "main_goal",
    options: [
      { value: "buy_home", label: "🏠 Buy a home", emoji: "🏠" },
      { value: "buy_car", label: "🚗 Buy a car", emoji: "🚗" },
      { value: "improve_credit", label: "📈 Improve my credit score", emoji: "📈" },
      { value: "get_loan", label: "💰 Get approved for a loan", emoji: "💰" },
      { value: "other", label: "✨ Something else", emoji: "✨" },
    ],
  },
  {
    id: "goal_response",
    type: "message",
    content: (data) => {
      const responses = {
        buy_home: "Buying a home is an exciting goal! We've helped thousands of people become mortgage-ready. 🏡",
        buy_car: "Getting a great rate on a car loan starts with good credit. We can help! 🚗",
        improve_credit: "That's a smart decision! Better credit opens so many doors. 📊",
        get_loan: "We'll work together to improve your chances of loan approval. 💪",
        other: "Whatever your goal, better credit will help you get there! ✨",
      };
      return responses[data.main_goal] || responses.other;
    },
    delay: 400,
  },
  {
    id: "credit_score_range",
    type: "choice",
    question: "Do you know roughly where your credit score falls?",
    field: "credit_score_range",
    options: [
      { value: "excellent", label: "🌟 750+ (Excellent)", emoji: "🌟" },
      { value: "good", label: "✅ 700-749 (Good)", emoji: "✅" },
      { value: "fair", label: "📊 650-699 (Fair)", emoji: "📊" },
      { value: "building", label: "🔧 Below 650 (Building)", emoji: "🔧" },
      { value: "unknown", label: "🤷 Not sure", emoji: "🤷" },
    ],
  },
  {
    id: "credit_response",
    type: "message",
    content: (data) => {
      if (data.credit_score_range === "building" || data.credit_score_range === "fair") {
        return "No worries! That's exactly what we're here for. Credit Firm Inc has helped thousands of people improve their scores! 💪";
      } else if (data.credit_score_range === "unknown") {
        return "No problem! We can help you figure that out. Knowledge is power! 📈";
      }
      return "Awesome! Let's work on maintaining and improving your credit profile! 🎯";
    },
    delay: 400,
  },
  {
    id: "timeline",
    type: "choice",
    question: "When are you hoping to achieve your goal?",
    field: "timeline",
    options: [
      { value: "asap", label: "🚀 As soon as possible", emoji: "🚀" },
      { value: "3_months", label: "📅 Within 3 months", emoji: "📅" },
      { value: "6_months", label: "📆 Within 6 months", emoji: "📆" },
      { value: "year", label: "🗓️ Within a year", emoji: "🗓️" },
      { value: "exploring", label: "🔍 Just exploring options", emoji: "🔍" },
    ],
  },
  {
    id: "email",
    type: "input",
    question: "Great! What's the best email to reach you?",
    placeholder: "your.email@example.com",
    field: "email",
    inputType: "email",
    required: false,
  },
  {
    id: "phone",
    type: "input",
    question: "And a phone number so our team can give you a call?",
    placeholder: "(555) 123-4567",
    field: "phone",
    inputType: "tel",
    required: false,
  },
  {
    id: "final_message",
    type: "message",
    content: (data) => `Perfect, ${data.first_name}! You're all set. Our credit counseling team will reach out soon to start your personalized plan! 🎉`,
    delay: 300,
  },
];

const CFIConversationalSurvey = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const processedStepsRef = useRef(new Set());

  const scrollToBottom = () => {
    // Only scroll within the messages container, not the entire page
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.closest('.overflow-y-auto');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize on mount - only run once
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      processStep(0);
    }
  }, [isInitialized]);

  const processStep = async (stepIndex) => {
    const step = SURVEY_STEPS[stepIndex];
    if (!step) return;

    // Prevent duplicate processing of the same step
    const stepKey = `${step.id}_${stepIndex}`;
    if (processedStepsRef.current.has(stepKey)) {
      return;
    }

    if (step.type === "message") {
      processedStepsRef.current.add(stepKey);
      setIsTyping(true);
      await new Promise((resolve) => setTimeout(resolve, step.delay || 500));
      
      const content = typeof step.content === "function" ? step.content(formData) : step.content;
      
      setMessages((prev) => {
        // Check if this message already exists
        if (prev.some(m => m.id === step.id)) {
          return prev;
        }
        return [...prev, { type: "bot", content, id: step.id }];
      });
      setIsTyping(false);
      
      // Auto-advance after message
      const nextStep = stepIndex + 1;
      setTimeout(() => {
        setCurrentStep(nextStep);
        // If next step is also a message, process it
        if (SURVEY_STEPS[nextStep]?.type === "message") {
          processStep(nextStep);
        }
      }, 800);
    } else {
      // For input and choice steps, just update currentStep
      setCurrentStep(stepIndex);
    }
  };

  const advanceToNextStep = (newFormData) => {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    
    // If next step is a message, process it with the updated form data
    const nextStepData = SURVEY_STEPS[nextStep];
    if (nextStepData?.type === "message") {
      processMessageStep(nextStep, newFormData);
    }
  };

  const processMessageStep = async (stepIndex, data) => {
    const step = SURVEY_STEPS[stepIndex];
    if (!step || step.type !== "message") return;

    const stepKey = `${step.id}_${stepIndex}`;
    if (processedStepsRef.current.has(stepKey)) {
      return;
    }
    processedStepsRef.current.add(stepKey);

    setIsTyping(true);
    await new Promise((resolve) => setTimeout(resolve, step.delay || 500));
    
    const content = typeof step.content === "function" ? step.content(data) : step.content;
    
    setMessages((prev) => {
      if (prev.some(m => m.id === step.id)) {
        return prev;
      }
      return [...prev, { type: "bot", content, id: step.id }];
    });
    setIsTyping(false);
    
    // Auto-advance after message
    const nextStep = stepIndex + 1;
    setTimeout(() => {
      setCurrentStep(nextStep);
      // If next step is also a message, process it
      if (SURVEY_STEPS[nextStep]?.type === "message") {
        processMessageStep(nextStep, data);
      }
    }, 800);
  };

  const handleInputSubmit = () => {
    const step = SURVEY_STEPS[currentStep];
    if (!step || step.type !== "input") return;

    if (step.required && !inputValue.trim()) {
      toast.error("Please enter a value");
      return;
    }

    // Add user message
    setMessages((prev) => [
      ...prev,
      { type: "user", content: inputValue, id: `${step.id}_response` },
    ]);

    // Update form data
    const newFormData = { ...formData, [step.field]: inputValue };
    setFormData(newFormData);
    setInputValue("");

    // Check if this is the last step before final message
    if (currentStep === SURVEY_STEPS.length - 2) {
      // Submit the form
      submitForm(newFormData);
    } else {
      advanceToNextStep(newFormData);
    }
  };

  const handleChoiceSelect = (option) => {
    const step = SURVEY_STEPS[currentStep];
    if (!step || step.type !== "choice") return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      { type: "user", content: option.label, id: `${step.id}_response` },
    ]);

    // Update form data
    const newFormData = { ...formData, [step.field]: option.value };
    setFormData(newFormData);

    advanceToNextStep(newFormData);
  };

  const submitForm = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/referrals/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone,
          betenbough_special_notes_or_credit_strategy: `Goal: ${data.main_goal}, Credit Range: ${data.credit_score_range}, Timeline: ${data.timeline}`,
          source: "cfi_conversational_survey",
        }),
      });

      if (response.ok) {
        // Show final message
        const nextStep = currentStep + 1;
        processMessageStep(nextStep, data);
        setTimeout(() => {
          onComplete?.(data);
        }, 2000);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepData = SURVEY_STEPS[currentStep];

  return (
    <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl mx-auto">
      {/* Chat Header */}
      <div className="bg-gradient-to-r from-[#357c2a] to-[#265324] text-white px-6 py-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
          <Bot className="h-5 w-5" />
        </div>
        <div>
          <p className="font-semibold">Credit Firm Inc Assistant</p>
          <p className="text-xs text-white/70">Usually replies instantly</p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50">
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={message.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.type === "user"
                    ? "bg-[#357c2a] text-white rounded-br-none"
                    : "bg-white shadow-sm border border-gray-100 text-gray-800 rounded-bl-none"
                }`}
              >
                {message.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing Indicator */}
        {isTyping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-none px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </motion.div>
        )}

        {/* Question/Input Area */}
        {currentStepData && currentStepData.type === "input" && !isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-none px-4 py-3 text-gray-800 max-w-[80%]">
              {currentStepData.question}
            </div>
          </motion.div>
        )}

        {/* Choice Buttons */}
        {currentStepData && currentStepData.type === "choice" && !isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl rounded-bl-none px-4 py-3 text-gray-800 max-w-[80%]">
              {currentStepData.question}
            </div>
            <div className="flex flex-wrap gap-2 pl-2">
              {currentStepData.options.map((option) => (
                <Button
                  key={option.value}
                  variant="outline"
                  size="sm"
                  onClick={() => handleChoiceSelect(option)}
                  className="rounded-full border-[#357c2a] text-[#357c2a] hover:bg-[#357c2a] hover:text-white transition-all"
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      {currentStepData && currentStepData.type === "input" && (
        <div className="p-4 border-t bg-white">
          <div className="flex gap-2">
            <Input
              type={currentStepData.inputType || "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={currentStepData.placeholder}
              onKeyPress={(e) => e.key === "Enter" && handleInputSubmit()}
              className="flex-1 rounded-full border-gray-200 focus:border-[#357c2a] focus:ring-[#357c2a]"
              disabled={isSubmitting}
            />
            <Button
              onClick={handleInputSubmit}
              disabled={isSubmitting}
              className="rounded-full bg-[#357c2a] hover:bg-[#265324] text-white px-4"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Completed State */}
      {currentStep >= SURVEY_STEPS.length && (
        <div className="p-4 border-t bg-white">
          <div className="flex items-center justify-center gap-2 text-[#357c2a]">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">All done! We&apos;ll be in touch soon.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CFIConversationalSurvey;
